const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  flag?: string;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
}

export interface ExchangeRate {
  from: string;
  to: string;
  rate: number;
  timestamp: string;
}

export interface ConversionResult {
  originalAmount: number;
  originalCurrency: string;
  convertedAmount: number;
  targetCurrency: string;
  rate: number;
  formatted: string;
}

async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('token');
  const url = `${API_BASE_URL}${endpoint}`;
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    credentials: 'include',
    ...options,
  };

  const response = await fetch(url, config);
  return response.json();
}

class CurrencyApiService {
  // Get all currencies
  async getCurrencies(): Promise<{ success: boolean; data: { currencies: Currency[] } }> {
    return apiFetch('/currency/currencies');
  }

  // Get default currency
  async getDefaultCurrency(): Promise<{ success: boolean; data: { currency: Currency } }> {
    return apiFetch('/currency/currencies/default');
  }

  // Get user's preferred currency
  async getUserCurrency(): Promise<{ success: boolean; data: { currency: Currency } }> {
    return apiFetch('/currency/user/currency');
  }

  // Update user's preferred currency
  async updateUserCurrency(currencyCode: string): Promise<{ 
    success: boolean; 
    data: { currency: Currency };
    message?: string;
  }> {
    return apiFetch('/currency/user/currency', {
      method: 'PUT',
      body: JSON.stringify({ currencyCode }),
    });
  }

  // Get exchange rate between two currencies
  async getExchangeRate(from: string, to: string): Promise<{ 
    success: boolean; 
    data: ExchangeRate;
    message?: string;
  }> {
    return apiFetch(`/currency/exchange-rate?from=${from}&to=${to}`);
  }

  // Convert amount between currencies
  async convert(amount: number, from: string, to: string): Promise<{ 
    success: boolean; 
    data: ConversionResult;
    message?: string;
  }> {
    return apiFetch('/currency/convert', {
      method: 'POST',
      body: JSON.stringify({ amount, from, to }),
    });
  }

  // Format amount with currency symbol
  formatAmount(amount: number, currencyCode: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  }

  // Format amount without currency code (just symbol)
  formatAmountShort(amount: number, currencyCode: string): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      notation: 'compact',
    });
    return formatter.format(amount);
  }
}

export const currencyApi = new CurrencyApiService();
export default currencyApi;
